import React, { useState, useEffect } from 'react'
import { Mail, Send } from 'lucide-react'
import { sendGmailEmail } from '../../lib/email'

export interface EmailReplyComposerProps {
  businessId: string
  to: string
  defaultSubject?: string
  placeholder?: string
  onSendSuccess?: (messageId: string) => void
  onSendError?: (error: string) => void
  disabled?: boolean
  className?: string
}

export function EmailReplyComposer({
  businessId,
  to,
  defaultSubject = '',
  placeholder = 'Type your email message here...',
  onSendSuccess,
  onSendError,
  disabled = false,
  className = ''
}: EmailReplyComposerProps) {
  const [subject, setSubject] = useState(defaultSubject)
  const [body, setBody] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset subject when defaultSubject changes
  useEffect(() => {
    setSubject(defaultSubject)
  }, [defaultSubject])

  // Clear error when user starts typing
  useEffect(() => {
    if ((subject || body) && error) {
      setError(null)
    }
  }, [subject, body, error])

  const handleSendEmail = async () => {
    if (!subject.trim() || !body.trim()) {
      setError('Please enter both subject and message')
      return
    }

    if (!to) {
      setError('No email address specified')
      return
    }

    setIsSending(true)
    setError(null)

    try {
      const result = await sendGmailEmail({
        businessId,
        to,
        subject: subject.trim(),
        body: body.trim()
      })

      if (result.success) {
        // Clear the inputs
        setSubject(defaultSubject)
        setBody('')
        
        // Call success callback
        if (onSendSuccess && result.messageId) {
          onSendSuccess(result.messageId)
        }
      } else {
        const errorMessage = result.error || 'Failed to send email'
        setError(errorMessage)
        if (onSendError) {
          onSendError(errorMessage)
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send email'
      setError(errorMessage)
      if (onSendError) {
        onSendError(errorMessage)
      }
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      if (!isSending && subject.trim() && body.trim()) {
        handleSendEmail()
      }
    }
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Email Subject */}
      <div>
        <input
          type="text"
          placeholder="Email subject..."
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          disabled={disabled || isSending}
          onKeyDown={handleKeyDown}
        />
      </div>

      {/* Email Body */}
      <div className="flex items-end space-x-3">
        <div className="flex-1">
          <textarea
            placeholder={placeholder}
            className="w-full resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={disabled || isSending}
            onKeyDown={handleKeyDown}
          />
        </div>
        
        {/* Send Button */}
        <button
          onClick={handleSendEmail}
          disabled={disabled || isSending || !subject.trim() || !body.trim()}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2 ${
            disabled || isSending || !subject.trim() || !body.trim()
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-purple-500 hover:bg-purple-600 text-white cursor-pointer'
          }`}
          title={isSending ? 'Sending...' : 'Send Email (Ctrl/Cmd + Enter)'}
        >
          {isSending ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
          ) : (
            <Mail className="h-4 w-4" />
          )}
          <span>{isSending ? 'Sending...' : 'Send'}</span>
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="text-red-600 text-xs bg-red-50 p-2 rounded border border-red-200">
          {error}
        </div>
      )}

      {/* Helper Text */}
      <div className="text-xs text-gray-500">
        Press Ctrl/Cmd + Enter to send • Sending to: {to}
      </div>
    </div>
  )
} 